import { type Components, type Theme } from "@mui/material/styles";

import { type TabSize, type TabVariant } from "../../components/Tabs";
import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";

declare module "@mui/material/Tab" {
  interface TabOwnProps {
    size?: TabSize;
    tabVariant?: TabVariant;
  }
}

declare module "@mui/material/Tabs" {
  interface TabsOwnProps {
    size?: TabSize;
    tabVariant?: TabVariant;
  }
}

export const muiTabsOverrides: Components<Theme> = {
  MuiTabs: {
    defaultProps: {
      size: "normal",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        maxWidth: "100%",
        overflowX: "auto",
        width: "fit-content",
        alignItems: "center",
        display: "flex",

        scrollbarWidth: "none",

        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none",

        "& .MuiTabs-flexContainer": {
          width: "100%",
          gap: theme.spacing(5),
        },

        "& .MuiTabs-scroller": {
          overflow: "visible !important",
          width: "100%",
          borderBottom: "none",
        },

        variants: [
          {
            props: { size: "normal" },
            style: {
              "& .MuiTabs-scroller": {},
              "& .MuiTabs-flexContainer": {
                height: theme.size?.tabs.normalHeight,
              },
            },
          },
          {
            props: { size: "large" },
            style: {
              "& .MuiTabs-flexContainer": {
                height: theme.size?.tabs.largeHeight,
              },
            },
          },
          {
            props: { tabVariant: "pill" },
            style: {
              padding: theme.spacing(1),
              "& .MuiTabs-flexContainer": {
                gap: theme.spacing(3),
              },
              "& .MuiTabs-scroller": {
                overflow: "visible !important",
              },
            },
          },
          {
            props: { tabVariant: "invertedPill" },
            style: {
              backgroundColor: theme.palette.background?.muted,
              borderRadius: theme.borderRadius?.large,
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: 0,
                padding: theme.spacing(1),
                height: "auto",
              },
            },
          },
        ],
      }),
      indicator: ({ theme, ownerState }) => {
        const tabsOwnerState = ownerState as { tabVariant?: string };
        const isPill =
          tabsOwnerState.tabVariant === "pill" || tabsOwnerState.tabVariant === "invertedPill";

        if (isPill) {
          return {
            display: "none",
          };
        }

        return {
          backgroundColor: theme.palette.primary.main,
          height: theme.size?.tabs.indicatorHeight,
          borderRadius: 0,
          top: "auto",
        };
      },
    },
  },
  MuiTab: {
    defaultProps: {
      size: "normal",
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        zIndex: 2,
        textTransform: "none",
        flexDirection: "row",
        whiteSpace: "nowrap",
        maxWidth: "none",
        width: "auto",
        minWidth: "auto",
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.text.subtle,
        flexGrow: 1,
        flexShrink: 0,
        padding: `${theme.spacing(3)} 0px`,

        "&.Mui-selected": {
          color: theme.palette.text.primary,
        },

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            color: theme.palette.text.primary,
          },
          defaultStyle: {
            color: theme.palette.text.subtle,
          },
        }),

        variants: [
          {
            props: { size: "normal" },
            style: {
              fontSize: theme.typography.body1.fontSize,
              minHeight: theme.size?.tabs.normalHeight,
            },
          },
          {
            props: { size: "large" },
            style: {
              fontSize: theme.typography.h6.fontSize,
              minHeight: theme.size?.tabs.largeHeight,
            },
          },
          {
            props: { tabVariant: "pill" },
            style: {
              padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
              borderRadius: theme.borderRadius?.xxLarge,
              position: "relative" as const,
              zIndex: 3,
              backgroundColor: "transparent",
              transition: "background-color 0.3s ease",

              "&.Mui-selected": {
                backgroundColor: theme.palette.background.inactive,
                color: theme.palette.text.primary,
              },
            },
          },
          {
            props: { tabVariant: "invertedPill" },
            style: {
              padding: `${theme.spacing(3)} ${theme.spacing(5)}`,
              borderRadius: theme.borderRadius?.large,
              zIndex: 3,
              color: theme.palette.base?.inactive,
              fontSize: theme.typography.body2.fontSize,

              "&.Mui-selected": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
            },
          },
        ],
      }),
    },
  },
};
